import { useEffect, useRef } from "react";
import { anyCheat, CHEAT_LABELS, CHEATS, type CheatId } from "../game/cheats";
import type { GapStatus, HudState, SettingsHud, StatsHud } from "../game/Game";
import { ALL_CONTROL_ROWS, choiceOfRow, parsePick, pauseItems, SETTINGS_SCREEN_LABELS, STEP_DOWN, type ControlRows, type PauseAction, type PauseScreen, type SettingsScreen } from "../game/pause";
import { ACTION_LABELS, CHOICE_KEYS, choiceLabel, defaultSettings, isListKey, keyLabel, remapped, padLabel, SETTING_ROWS, TOUCH_MODE_LABELS, TOUCH_STEER_LABELS, TOUCH_OPACITY_MAX, TOUCH_OPACITY_MIN, VOLUME_MAX, type Action, type ListKey, type Settings } from "../game/settings";
import { SKATERS } from "../game/skaters";
import { defaultSpecials, RIDE_SPECIALS, seqLabel, seqOf, tapLabel } from "../game/special-seqs";
import { DEFAULT_STATS, type StatId } from "../game/stats";
import type { SpecialTrick } from "../game/tricks";
import { STYLES } from "../game/render/styles";
import { SkaterCards } from "./SkaterSelect";
import type { Tier } from "../game/ladder";
import type { Goal } from "../game/run";
import { GoalList } from "./GoalList";
import { StatPips } from "./StatPips";
import { choiceDetail as detailOf } from "./choice-detail";
import type { InputKind } from "./input-names";
import { RELEASE_NOTES } from "../game/release-notes";
import { DayNotes, NewDot } from "./WhatsNew";
import { DIALOG, NOTE, ROW, ROW_ON, SCRIM, TITLE } from "./theme";

/** The current map's gaps, checked off as you land them (this session only). */
function GapList({ gaps }: { gaps: readonly GapStatus[] }) {
  if (gaps.length === 0) return <p className="mb-3 text-center text-sm font-bold opacity-70">This map has no gaps yet.</p>;
  const found = gaps.filter((g) => g.found).length;
  return (
    <section className="mb-3">
      <p className="text-center text-sm font-black uppercase italic tracking-widest">
        {found} of {gaps.length} found
      </p>
      <ul aria-label="Gaps" className="mt-2 flex flex-col gap-1">
        {gaps.map((g) => (
          <li
            key={g.name}
            className={`flex items-baseline justify-between gap-3 rounded-xl border-2 border-ink px-3 py-1 text-sm ${g.found ? "bg-aqua-soft text-ink" : "bg-surface-soft opacity-70"}`}
          >
            <span className="min-w-0 font-black uppercase italic">
              <span aria-hidden="true" className="mr-2 inline-block w-3">
                {g.found ? "✓" : "·"}
              </span>
              {g.name}
              <span className="sr-only">{g.found ? " (found)" : " (not found)"}</span>
            </span>
            <span className="shrink-0 font-black tabular-nums">{g.points.toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}


/** Pause > Skater's card for the highlighted row (on Back, the one you ride): the one you ride shows their sheet, the others their starting spread. */
function HighlightedSkater({ index, riding, stats }: { index: number; riding: string; stats: StatsHud }) {
  return (
    <section className="mt-3 border-t-2 border-line pt-3">
      <SkaterCards shown={SKATERS[index]?.id ?? riding} riding={riding} stats={stats.stats} />
    </section>
  );
}

/** The volume as ten pips. */
function VolumePips({ value }: { value: number }) {
  return (
    <span className="ml-2 inline-flex items-center gap-0.5 align-middle" aria-label={`${value} of ${VOLUME_MAX}`}>
      {Array.from({ length: VOLUME_MAX }, (_, i) => (
        <span key={i} aria-hidden="true" className={`inline-block h-3 w-2 rounded-sm border border-ink ${i < value ? "bg-on-surface" : "bg-line"}`} />
      ))}
    </span>
  );
}

/** Each screen's heading; a Settings pick list is titled after its row. */
const TITLES: Record<Exclude<PauseScreen, ListKey>, string> = {
  main: "Paused",
  maps: "Change map",
  gaps: "Gap list",
  goals: "Goals",
  stats: "Stats",
  skater: "Skater",
  settings: "Settings",
  ...SETTINGS_SCREEN_LABELS,
  cheats: "Cheats",
  specials: "Specials",
  news: "What's new",
};
const titleOf = (screen: PauseScreen): string => (isListKey(screen) ? SETTING_ROWS[screen].label : TITLES[screen]);

/** A Settings row's one-line summary of its screen's current values (#126). */
function screenSummary(screen: SettingsScreen, s: Settings, controls: ControlRows): string {
  if (screen === "input") {
    const parts = [];
    if (controls.buttons) parts.push(remapped(s) ? "Buttons remapped" : "Default buttons");
    if (!defaultSpecials(s.specials)) parts.push("Specials changed");
    if (controls.touch) parts.push(`Touch controls: ${TOUCH_MODE_LABELS[s.touch.mode]}`);
    return parts.join(" · ");
  }
  const values = CHOICE_KEYS.filter((k) => SETTING_ROWS[k].group === screen).map((k) => `${SETTING_ROWS[k].label}: ${choiceLabel(k, s[k])}`);
  return (screen === "soundCamera" ? [`Volume: ${s.volume} of ${VOLUME_MAX}`, ...values] : values).join(" · ");
}

/** A row's "−" button (take a stat point back, step a setting down), if it has one. */
function downOf(action: PauseAction, stats: StatsHud, settings: SettingsHud): { action: PauseAction; disabled: boolean } | null {
  if (action.startsWith("stat:")) {
    const id = statOf(action);
    return { action: `unstat:${id}`, disabled: stats.stats[id] <= stats.base[id] };
  }
  // A special's row steps back through the free sequences (#135).
  if (action.startsWith("special:")) return { action: `special-:${action.slice("special:".length)}`, disabled: false };
  // The hand row and the cheats are switches: the row itself flips it, no "−".
  const down = action === "touchHand" || action.startsWith("cheat:") ? undefined : STEP_DOWN[action];
  if (!down) return null;
  const s = settings.settings;
  const disabled = (action === "volume+" && s.volume <= 0) || (action === "touchOpacity+" && s.touch.opacity <= TOUCH_OPACITY_MIN);
  return { action: down, disabled };
}

/** A settings row's label with its current value, or null for rows that show it another way. */
function settingLabel(action: PauseAction, s: Settings): string | null {
  // A pick-one setting: its name and choice.
  const key = choiceOfRow(action);
  if (key) return `${SETTING_ROWS[key].label}: ${choiceLabel(key, s[key])}`;
  switch (action) {
    case "touchMode+":
      return `Touch controls: ${TOUCH_MODE_LABELS[s.touch.mode]}`;
    case "touchSize+":
      return `Button size: ${s.touch.size}`;
    case "touchSteer+":
      return `Steering sensitivity: ${TOUCH_STEER_LABELS[s.touch.steer]}`;
    case "touchOpacity+":
      return `Button opacity: ${Math.round((s.touch.opacity / TOUCH_OPACITY_MAX) * 100)}%`;
    case "touchHand":
      return `Left-handed: ${s.touch.left ? "On" : "Off"}`;
    case "cheats": {
      const on = CHEATS.filter((id) => s.cheats[id]).length;
      return on ? `Cheats: ${on} on` : "Cheats: Off";
    }
    case "specials":
      return defaultSpecials(s.specials) ? "Specials: Default" : "Specials: Changed";
    default:
      if (action.startsWith("cheat:")) {
        const id = action.slice("cheat:".length) as CheatId;
        return `${CHEAT_LABELS[id]}: ${s.cheats[id] ? "On" : "Off"}`;
      }
      return null;
  }
}

/** The line under a Settings pick list's choice: a style's pitch, what a quality or an effect does. */
function choiceDetail(action: PauseAction): string | null {
  const pick = parsePick(action);
  return pick ? (detailOf(pick.key, pick.id) ?? null) : null;
}

/** The choice in use, marked in its pick list. */
function isCurrentChoice(action: PauseAction, s: Settings): boolean {
  const pick = parsePick(action);
  return pick !== null && s[pick.key] === pick.id;
}

/** The line under a special's row (#135): its taps and button, the taps so far while waiting, or why the last pair was refused. */
function specialLine(t: SpecialTrick, hud: SettingsHud): string {
  const edit = hud.special?.name === t.name ? hud.special : null;
  const button = ACTION_LABELS[t.button];
  if (edit?.taps) return `Tap two stick directions… ${edit.taps.map(tapLabel).join(" ")} (Start / Esc / ○ cancels)`;
  const line = `${seqLabel(seqOf(hud.settings.specials, t))} + ${button}`;
  return edit?.note ? `${edit.note}: kept ${line}` : line;
}

/** The stat a Stats screen row is for. */
function statOf(action: PauseAction): StatId {
  return action.slice("stat:".length) as StatId;
}

/** The pause menu's view of the HUD: everything `PauseOverlay` shows but its callbacks. */
export function pauseOverlayProps(hud: HudState) {
  return {
    index: hud.pauseIndex,
    screen: hud.pauseScreen,
    mapId: hud.mapId,
    gaps: hud.gaps,
    goals: hud.goals,
    hubs: hud.pauseHubs,
    hasHubs: hud.hasHubs,
    demo: hud.demo !== null,
    run: hud.run !== null,
    challenge: hud.challenge !== null,
    settings: hud.settings,
    stats: hud.stats,
    skater: hud.skater,
    applying: hud.applying,
    controls: hud.pauseControls,
  };
}

/** The pause menu. The game drives the highlight from the controller; the mouse works too. */
export function PauseOverlay({
  index,
  screen = "main",
  mapId,
  gaps = [],
  goals = [],
  hubs = [],
  hasHubs = false,
  demo = false,
  run = false,
  challenge = false,
  settings = { settings: defaultSettings(), rebinding: null },
  stats = { stats: DEFAULT_STATS, base: DEFAULT_STATS, points: 0 },
  skater = SKATERS[0]!.id,
  applying = false,
  controls = ALL_CONTROL_ROWS,
  input = "keyboard",
  newNotes = false,
  onNotesSeen,
  onPick,
  onHover,
}: {
  index: number;
  screen?: PauseScreen;
  /** The map being skated, marked in the map list. */
  mapId?: string;
  /** The current map's gaps, for the Gap list screen. */
  gaps?: readonly GapStatus[];
  /** The goal list, for the Goals screen. */
  goals?: readonly Goal[];
  /** The open hubs' tiers: in free skate, a "Go to hub" row each on the Challenges screen (#87). */
  hubs?: readonly Tier[];
  /** The map has challenge hubs (#86): the Challenges screen is laid out by hub. */
  hasHubs?: boolean;
  /** A demo is playing, so the demo item stops it. */
  demo?: boolean;
  /** A 2-minute run is on, so the run item ends it. */
  run?: boolean;
  /** A challenge is on: retry and quit replace map, run and demo switching. */
  challenge?: boolean;
  /** Volume, camera and bindings, for the Settings screen. */
  settings?: SettingsHud;
  /** The session's stats and points to spend, for the Stats screen. */
  stats?: StatsHud;
  /** Who's being ridden, marked on the Skater screen. */
  skater?: string;
  /** A new art style or quality is being built: "Applying…" so the menu doesn't look stuck. */
  applying?: boolean;
  /** Which control rows this device gets on Settings > Controls (#126). */
  controls?: ControlRows;
  /** The input you're using (#147): the goal list names its buttons. */
  input?: InputKind;
  /** There are release notes the player hasn't seen (#159): a dot on What's new. */
  newNotes?: boolean;
  /** What's new is open: its notes count as seen. */
  onNotesSeen?: () => void;
  onPick: (action: PauseAction) => void;
  onHover: (index: number) => void;
}) {
  const mode = challenge ? "challenge" : run ? "run" : "free";
  // "Go to hub" rows (#87) go above the Challenges list, so the highlight starts in view.
  const hubsFirst = screen === "goals" && mode === "free" && hubs.length > 0;
  useEffect(() => {
    if (screen === "news") onNotesSeen?.();
  }, [screen, onNotesSeen]);
  // What's new is taller than the dialog: keep the highlighted day in view as the stick moves.
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (screen === "news") listRef.current?.querySelector('[aria-current="true"]')?.scrollIntoView?.({ block: "nearest" });
  }, [screen, index]);
  const menu = (
    <ul ref={listRef} className="flex flex-col gap-2">
      {pauseItems(screen, mode, hubs, controls).map((item, i) => {
        const down = downOf(item.action, stats, settings);
        const bind = item.action.startsWith("bind:") ? (item.action.slice("bind:".length) as Action) : null;
        const special = item.action.startsWith("special:") ? RIDE_SPECIALS.find((s) => `special:${s.trick.name}` === item.action)!.trick : null;
        return (
        <li key={item.action} className={down ? "flex gap-2" : undefined}>
          <button
            type="button"
            onClick={() => onPick(item.action)}
            // Only real mouse movement moves the highlight: a cursor that happens to be
            // resting where the menu pops up shouldn't pick "Reset skater" for you.
            onPointerMove={(e) => {
              if ((e.movementX || e.movementY) && i !== index) onHover(i);
            }}
            aria-current={i === index ? "true" : undefined}
            className={`w-full rounded-md border-2 px-4 py-2 text-center text-base font-black uppercase italic tracking-wide focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-focus ${
              i === index ? ROW_ON : `${ROW} hover:border-accent`
            }`}
          >
            {item.action === "demo" && demo
              ? "Stop demo"
              : item.action === "run" && run
                ? "End run"
                : item.action === "stats" && stats.points > 0
                  ? `Stats (${stats.points})`
                  : (settingLabel(item.action, settings.settings) ?? item.label)}
            {item.action === "news" && newNotes && <NewDot />}
            {item.action.startsWith("news:") && <DayNotes day={RELEASE_NOTES.find((d) => `news:${d.date}` === item.action)!} />}
            {item.action.startsWith("stat:") && <StatPips id={statOf(item.action)} value={stats.stats[statOf(item.action)]} base={stats.base} />}
            {item.action === "volume+" && <VolumePips value={settings.settings.volume} />}
            {item.action === "artStyle" && (
              <span className="block text-xs font-semibold normal-case not-italic tracking-normal opacity-75">
                {applying ? "Applying…" : STYLES[settings.settings.artStyle].pitch}
              </span>
            )}
            {isCurrentChoice(item.action, settings.settings) && (
              <span className="ml-2 align-middle text-xs not-italic opacity-70">(current)</span>
            )}
            {item.action.startsWith("group:") && (
              <span className="block text-xs font-semibold normal-case not-italic tracking-normal opacity-75">
                {screenSummary(item.action.slice("group:".length) as SettingsScreen, settings.settings, controls)}
              </span>
            )}
            {choiceDetail(item.action) && (
              <span className="block text-xs font-semibold normal-case not-italic tracking-normal opacity-75">{choiceDetail(item.action)}</span>
            )}
            {special && (
              <span className="block text-xs font-semibold normal-case not-italic tracking-normal opacity-75">
                {specialLine(special, settings)}
              </span>
            )}
            {bind && (
              <span className="block text-xs font-semibold normal-case not-italic tracking-normal opacity-75">
                {settings.rebinding === bind
                  ? "Press a pad button or key… (Start / Esc cancels)"
                  : `${padLabel(settings.settings.pad[bind])} · ${settings.settings.keys[bind].map(keyLabel).join(" / ")}`}
              </span>
            )}
            {item.action === `skater:${skater}` && (
              <span className="ml-2 align-middle text-xs not-italic opacity-70">(riding)</span>
            )}
            {item.action === `map:${mapId}` && (
              <span className="ml-2 align-middle text-xs not-italic opacity-70">(current)</span>
            )}
            {item.detail && (
              <span className="block text-xs font-semibold normal-case not-italic tracking-normal opacity-75">{item.detail}</span>
            )}
          </button>
          {down && (
            <button
              type="button"
              onClick={() => onPick(down.action)}
              disabled={down.disabled}
              aria-label={item.action.startsWith("stat:") ? `Take a point back off ${item.label}` : `${item.label} down`}
              className="shrink-0 rounded-md border-2 border-line bg-surface-soft px-3 text-center text-lg font-black hover:border-accent focus-visible:outline-3 focus-visible:outline-focus disabled:opacity-30"
            >
              −
            </button>
          )}
        </li>
        );
      })}
    </ul>
  );
  return (
    <div className={`absolute inset-0 grid place-items-center ${SCRIM} p-4`}>
      <div role="dialog" aria-label="Paused" className={`${DIALOG} max-h-[calc(100dvh-2rem)] w-[min(90vw,28rem)] overflow-y-auto`}>
        <h2 className={`${TITLE} mb-4 text-center text-3xl`}>
          {screen === "goals" && !run ? "Challenges" : titleOf(screen)}
        </h2>
        {applying && (
          <p role="status" className="mb-3 rounded-md border-2 border-ink bg-highlight px-3 py-1 text-center text-sm font-black uppercase italic text-ink">
            Applying…
          </p>
        )}
        {screen === "gaps" && <GapList gaps={gaps} />}
        {hubsFirst && menu}
        {screen === "goals" && (hubsFirst ? <div className="mt-3"><GoalList goals={goals} run={run} hubs={hasHubs} input={input} /></div> : <GoalList goals={goals} run={run} hubs={hasHubs} input={input} />)}
        {screen === "stats" && (
          <p className="mb-3 text-center text-sm font-black uppercase italic tracking-widest">
            {stats.points === 1 ? "1 point to spend" : `${stats.points} points to spend`}
          </p>
        )}
        {!hubsFirst && menu}
        {screen === "skater" && <HighlightedSkater index={index} riding={skater} stats={stats} />}
        <p className={`${NOTE} mt-3 text-center`}>
          {screen === "maps"
            ? "Stick / arrows to move · ✕ or Space to pick · ○ or K to go back · Switching starts a new session"
            : screen === "news"
              ? "Stick / arrows to scroll · ○ or K to go back"
            : screen === "gaps"
              ? "Found gaps last until you reload · ○ or K to go back"
              : screen === "settings"
                ? "✕ or Space opens one · Saved when the browser allows · ○ or K to go back"
              : screen === "look" || screen === "effects"
                ? "✕ or Space lists every choice · ○ or K to go back"
              : screen === "soundCamera"
                ? "✕ or → turns up · ← turns down · ○ or K to go back"
              : screen === "input"
                ? [
                    controls.touch && "Touch controls: Auto shows them on a touchscreen · ←/→ changes a touch row",
                    controls.buttons && "✕ on an action, then press its new button or key (swaps with whatever had it)",
                    "○ to go back",
                  ]
                    .filter(Boolean)
                    .join(" · ")
              : isListKey(screen)
                ? "Stick / arrows to look through them · ✕ or Space to use one · ○ or K to go back unchanged"
              : screen === "specials"
                ? [
                    controls.buttons ? "✕ on a special, then tap its two stick directions" : "Tap a special to step its sequence",
                    "←/→ or − steps through the free ones · Meter full: the taps, then the button, in the air (grind: onto a rail) · ○ to go back",
                  ].join(" · ")
              : screen === "cheats"
                ? anyCheat(settings.settings.cheats)
                  ? "✕ or ←/→ turns one on or off · Cheats on: goals don't count and earn no stat points · ○ or K to go back"
                  : "✕ or ←/→ turns one on or off · With any on, goals don't count and earn no stat points · ○ or K to go back"
              : screen === "skater"
                ? "✕ or Space rides the highlighted skater · Stat points are shared, spent per skater · ○ or K to go back"
              : screen === "stats"
                ? "✕ or → spends a point · ← takes one back · Earn points from challenges · ○ or K to go back"
              : screen === "goals"
                ? run
                  ? "Goals reset with each run · ○ or K to go back"
                  : hubs.length
                    ? "Go to hub takes you to its tier's hub · Checks last until you reload · ○ or K to go back"
                    : "Checks last until you reload · ○ or K to go back"
              : "Stick / arrows to move · ✕ or Space to pick · Start or Esc to resume"}
        </p>
      </div>
    </div>
  );
}
